'use client'

import { useNavigate } from '@tanstack/react-router'
import { Sparkles, AlertTriangle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDemoStatus } from '@/hooks/useDemoStatus'
import { useTranslations } from '@/hooks/useTranslations'
import { canShowPurchaseUi } from '@/lib/purchase-surface'
import type { DemoStatus } from '@/hooks/useDemoStatus'

/** Which translation keys the blocked banner should use, or null to render nothing. */
export interface BlockedBannerCopy {
  titleKey: 'blockedTitle' | 'lapsedTitle'
  reasonKey: 'reasonAnalyses' | 'reasonExpired' | 'reasonSubscription'
  bodyKey: 'blockedWeb' | 'blockedNative' | 'lapsedWeb' | 'lapsedNative'
  ctaKey: 'cta' | 'ctaRenew'
}

/**
 * Pure copy decision for the blocked banner — extracted so it can be tested
 * without rendering (same approach as resolveStickySync).
 *
 * The distinction that matters: a lapsed PAYING customer must never be told
 * "Demo beendet". They never had a demo, and the wording is "reactivate", not
 * "purchase". Returns null when the banner must stay silent.
 */
export function resolveBlockedCopy(
  blockReason: DemoStatus['blockReason'],
  showPurchase: boolean,
): BlockedBannerCopy | null {
  // pending/rejected get AccountGate's full-screen state; if one slips through,
  // a paywall would be the wrong thing to show.
  if (blockReason === 'pending' || blockReason === 'rejected') return null

  const lapsedCustomer = blockReason === 'subscription'

  return {
    titleKey: lapsedCustomer ? 'lapsedTitle' : 'blockedTitle',
    reasonKey: blockReason === 'analyses'
      ? 'reasonAnalyses'
      : lapsedCustomer ? 'reasonSubscription' : 'reasonExpired',
    bodyKey: lapsedCustomer
      ? (showPurchase ? 'lapsedWeb' : 'lapsedNative')
      : (showPurchase ? 'blockedWeb' : 'blockedNative'),
    ctaKey: lapsedCustomer ? 'ctaRenew' : 'cta',
  }
}

/**
 * Usage/upsell banner. Unrestricted full accounts render nothing.
 *
 *   demo, running   → "Noch X von N Analysen · noch Y Tage" (amber near a limit)
 *   demo, blocked   → red paywall
 *   paid, locked    → red "subscription ended" notice
 *
 * Server-authoritative counts via useDemoStatus (GET /api/auth/me).
 *
 * Two things that used to be wrong:
 *
 *   * The paywall keyed off accountStatus === 'locked', but that status is only
 *     persisted once the user actually attempts an analysis. An account whose
 *     trial had already expired kept showing the friendly blue banner until it
 *     tried (and failed) to analyse something. It now keys off `blocked`, which
 *     is computed live.
 *   * With demo_analysis_limit = 0 the usage ratio is 0, so `nudge` stayed
 *     false and the banner offered no way forward at all. Blocked is now its
 *     own branch, ahead of any ratio maths.
 *
 * On native there is no buy button and no price — see canShowPurchaseUi().
 */
export function DemoStatusBanner() {
  const { demo } = useDemoStatus()
  const navigate = useNavigate()
  const t = useTranslations('demoBanner')

  if (!demo) return null
  // Full accounts only surface here when their subscription has ended.
  if (!demo.isDemo && !demo.blocked) return null

  const showPurchase = canShowPurchaseUi()
  const goUpgrade = () => void navigate({ to: '/upgrade' as never })

  if (demo.blocked) {
    const copy = resolveBlockedCopy(demo.blockReason, showPurchase)
    if (!copy) return null

    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-none mt-0.5" />
          <div className="text-sm text-red-800">
            <strong>{t(copy.titleKey)}</strong>{' '}
            {t(copy.reasonKey)}{' '}
            {t(copy.bodyKey)}
          </div>
        </div>
        {showPurchase && (
          <Button size="sm" onClick={goUpgrade} className="flex-none bg-[#001c4d] hover:bg-[#001c4d]/90">
            {t(copy.ctaKey)}
          </Button>
        )}
      </div>
    )
  }

  // Running demo — usage summary + a gentle nudge as the limits approach.
  const used = demo.analysesUsed
  const limit = demo.analysesLimit
  const remaining = limit != null ? Math.max(0, limit - used) : null
  const days = demo.daysLeft
  const usageRatio = limit != null && limit > 0 ? used / limit : 0
  // The ratio alone never fires on the small budgets demo accounts actually
  // get: with a limit of 2, one analysis used is 50% (below the threshold) and
  // two is already blocked — so the nudge had no window at all. `remaining <= 1`
  // gives every limit a last-call state regardless of size.
  const nudge = usageRatio >= 0.7 || (remaining != null && remaining <= 1) || (days != null && days <= 1)

  const tone = nudge
    ? 'border-amber-200 bg-amber-50 text-amber-900'
    : 'border-blue-200 bg-blue-50 text-blue-900'
  const Icon = nudge ? Clock : Sparkles

  return (
    <div className={`mb-4 rounded-lg border px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 ${tone}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0 text-sm">
        <Icon className="w-4 h-4 flex-none" />
        <span>
          <strong>{t('title')}</strong>{' '}
          {remaining != null && t('analysesLeft', { remaining, limit })}
          {remaining != null && days != null && ' · '}
          {days != null && t('daysLeft', { days })}
          {nudge && ` — ${showPurchase ? t('nudgeWeb') : t('nudgeNative')}`}
        </span>
      </div>
      {/* Always offered on the web, not just once the limits get close: a demo
          user who has already decided to buy should not have to burn through
          the rest of the trial first. Stays absent on native — a buy button
          inside the binary is the one thing 3.1.1 will not forgive. */}
      {showPurchase && (
        <Button
          size="sm"
          variant={nudge ? 'default' : 'outline'}
          onClick={goUpgrade}
          className="flex-none"
        >
          {t('cta')}
        </Button>
      )}
    </div>
  )
}
